import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { X } from 'lucide-react';
import DDICommentsSection from './DDICommentsSection';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useEffect, useRef, useState } from 'react';
import { displayError } from '@/lib/error';
import { EMOJI_REGEX } from '@/utils/constant';
import { addDDIWorkOrderComment, getDDIWorkOrderComments } from '@/services/dueDiligenceService';
import type { DdiWorkOrderComment } from '@/types/dueDiligence.types';
import { useUserStore } from '@/stores/user';

interface DDICommentsDialogProps {
  workOrderId: string;
  open: boolean;
  onClose: () => void;
  onCountUpdate?: (workOrderId: string, count: number) => void;
}

const DDICommentsDialog = ({ workOrderId, open, onClose, onCountUpdate }: DDICommentsDialogProps) => {
  const [comment, setComment] = useState('');
  const [commentsData, setCommentsData] = useState<DdiWorkOrderComment[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const currentUser = useUserStore((state) => state.user);

  useEffect(() => {
    if (!workOrderId) return;

    let ignore = false;

    const fetchComments = async () => {
      try {
        setIsFetching(true);
        setHasLoadError(false);
        const { data, error } = await getDDIWorkOrderComments(workOrderId);
        if (ignore) return;
        if (error) {
          displayError(error, 5000);
          setHasLoadError(true);
          return;
        }
        const fetched = data?.comments ?? [];
        setCommentsData(fetched);
        onCountUpdate?.(workOrderId, data?.totalCount ?? fetched.length);
      } finally {
        if (!ignore) setIsFetching(false);
      }
    };

    void fetchComments();

    return () => {
      ignore = true;
    };
  }, [workOrderId]);

  const saveHandler = async () => {
    if (!comment.trim() || isAddingComment) return;
    setIsAddingComment(true);

    const { data, error } = await addDDIWorkOrderComment(workOrderId, comment);

    if (error) {
      displayError(error, 5000);
      setIsAddingComment(false);
      return;
    }

    if (data) {
      const newComment: DdiWorkOrderComment = {
        commentId: data.commentId,
        workOrderId: data.workOrderId,
        commentText: data.commentText,
        createdBy: { userId: currentUser?.userId ?? data.createdBy.userId, name: data.createdBy.name },
        createdAt: data.createdAt,
      };
      setCommentsData((prev) => {
        const updated = [...prev, newComment];
        onCountUpdate?.(workOrderId, updated.length);
        return updated;
      });
    }

    setComment('');
    setIsAddingComment(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <Sheet modal={false} open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent
        hideCloseButton={true}
        className="w-[80vw] md:w-[500px] scrollbar-hide bg-white flex flex-col gap-0"
        side="right"
        tabIndex={-1}
      >
        <SheetHeader className="flex flex-col gap-4 p-6 pb-0 sticky top-0 bg-white">
          <SheetTitle className="flex flex-col gap-4 bg-white pb-0">
            <div className="flex flex-row justify-between">
              <span className="font-semibold text-xl">Comments</span>
              <div className="flex flex-row gap-2 h-[32px] [&_button]:border-[1px] [&_button]:p-[6px] [&_button]:rounded-md [&_button]:justify-center">
                <button type="button" aria-label="Close" className="flex items-center justify-center" onClick={onClose}>
                  <X color="#676767" className="h-[20px] w-[20px] cursor-pointer" />
                </button>
              </div>
            </div>
          </SheetTitle>
          <SheetDescription className="sr-only">Comments section</SheetDescription>
        </SheetHeader>
        <div className="flex flex-col h-full justify-between">
          <div className="py-2 h-full">
            {hasLoadError && !isFetching ? (
              <div className="w-full h-full flex justify-center items-center text-destructive font-base text-center px-4">
                Failed to load comments. Please try again.
              </div>
            ) : (
              <DDICommentsSection comments={commentsData} isFetching={isFetching} />
            )}
          </div>
          <div className="sticky bottom-0 p-2 bg-white">
            <div className="flex flex-row justify-between border-[1px] items-center border-bordercolor rounded-md gap-2 p-2 h-[64px]">
              <Textarea
                ref={inputRef}
                textAreaClassName="resize-none h-10 border-[0px] pt-[10px] px-2"
                className="border-none w-full p-0"
                onChange={(e) => {
                  const filteredValue = e.target.value.replace(EMOJI_REGEX, '');
                  e.target.value = filteredValue;
                  setComment(filteredValue.replace(/^\s+/, ''));
                }}
                value={comment}
                placeholder="Type your comment..."
              />
              <Button
                variant="outline"
                className="text-[#fff] h-[36px] w-[103px] py-2 px-4 rounded-md bg-primary border-primary hover:bg-[#12375C] hover:text-white"
                disabled={!comment || isAddingComment}
                isLoading={isAddingComment}
                onClick={saveHandler}
              >
                Add
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default DDICommentsDialog;
